import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { advanceService } from '../services/api';
import { CheckCircle, XCircle, Clock, User, DollarSign, AlertTriangle, Bell, ChevronLeft, ChevronRight, Plus, Square, CheckSquare, Search, Filter, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ManualAdvanceModal from './ManualAdvanceModal';
import ConfirmModal from './ConfirmModal';

const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const AdvanceApproval = () => {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('pending');
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [processingIds, setProcessingIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showManualModal, setShowManualModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [approvedFilters, setApprovedFilters] = useState({
    ref_month: '',
    ref_year: '',
    date_from: '',
    date_to: '',
  });

  const approvedPage = activeTab === 'approved' ? page : 1;

  const { data: approvedData, isLoading: approvedLoading } = useQuery({
    queryKey: ['approved-advance-requests', approvedFilters, page, pageSize],
    queryFn: () => advanceService.getApprovedRequests({
      ...Object.fromEntries(Object.entries(approvedFilters).filter(([_, v]) => v !== '')),
      page,
      limit: pageSize,
    }),
    enabled: activeTab === 'approved',
  });

  const approvedRequests = approvedData?.data?.data || [];
  const approvedPagination = approvedData?.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 };

  const { data: requestsData, isLoading } = useQuery({
    queryKey: ['pending-advance-requests', page, pageSize],
    queryFn: () => advanceService.getPendingRequests(page, pageSize),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const requests = requestsData?.data?.data || [];
  const pagination = requestsData?.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 };
  const stats = requestsData?.data?.stats || { total: 0, near_deadline: 0 };

  const bulkApproveMutation = useMutation({
    mutationFn: (requestIds) => advanceService.bulkApproveRequests(requestIds),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Solicitações aprovadas com sucesso!');
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['pending-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['approved-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-advance-request'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar solicitações');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (requestId) => advanceService.approveRequest(requestId),
    onSuccess: (_, requestId) => {
      toast.success('Solicitação aprovada com sucesso!');
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['pending-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['approved-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-advance-request'] });
    },
    onError: (error, requestId) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar solicitação');
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }) => advanceService.rejectRequest(requestId, { rejectionReason: reason }),
    onSuccess: (_, { requestId }) => {
      toast.success('Solicitação rejeitada.');
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
      setRejectionReasons(prev => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['pending-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['approved-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-advance-request'] });
    },
    onError: (error, { requestId }) => {
      toast.error(error.response?.data?.message || 'Erro ao rejeitar solicitação');
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ requestId, reason }) => advanceService.cancelApprovedRequest(requestId, { reason }),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Adiantamento cancelado e estornado no Caixa com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['pending-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['approved-advance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-advance-request'] });
      queryClient.invalidateQueries({ queryKey: ['company-cashbox-balance'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-transactions'] });
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao cancelar adiantamento');
    },
  });

  const handleApprove = (request) => {
    setConfirmAction({ type: 'approve', request });
  };

  const handleReject = (request) => {
    const reason = rejectionReasons[request.id] || '';
    if (!reason.trim()) {
      toast.error('Por favor, informe o motivo da rejeição');
      return;
    }
    setConfirmAction({ type: 'reject', request });
  };

  const handleReasonChange = (requestId, value) => {
    setRejectionReasons(prev => ({ ...prev, [requestId]: value }));
  };

  const isProcessing = (requestId) => processingIds.has(requestId);

  const toggleSelect = (requestId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === requests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(requests.map(r => r.id)));
    }
  };

  const handleBulkApprove = () => {
    if (selectedIds.size === 0) {
      toast.error('Selecione pelo menos uma solicitação');
      return;
    }
    setConfirmAction({ type: 'bulkApprove' });
  };

  const handleFilterChange = (key, value) => {
    setApprovedFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setApprovedFilters({ ref_month: '', ref_year: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const hasActiveFilters = Object.values(approvedFilters).some(v => v !== '');

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Aprovação de Adiantamentos</h1>
          <p className="text-xs sm:text-sm text-gray-600">Aprove ou rejeite solicitações</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {bulkApproveMutation.isPending ? 'Aprovando...' : `Aprovar (${selectedIds.size})`}
            </button>
          )}
          <button
            onClick={() => setShowManualModal(true)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Lançar</span>
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-4 sm:gap-6">
          <button
            onClick={() => { setActiveTab('pending'); setPage(1); }}
            className={`py-2 sm:py-3 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors ${
              activeTab === 'pending'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-1.5 sm:gap-2">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              Pendentes
              {stats.total > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                  {stats.total}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => { setActiveTab('approved'); setPage(1); }}
            className={`py-2 sm:py-3 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors ${
              activeTab === 'approved'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-1.5 sm:gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              Aprovados
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'pending' && requests.length > 0 && (
        <div className={`rounded-lg p-3 border-2 text-xs sm:text-sm ${
          stats.near_deadline > 0
            ? 'bg-red-50 border-red-300'
            : 'bg-yellow-50 border-yellow-300'
        }`}>
          <div className="flex items-start gap-2">
            <Bell className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              stats.near_deadline > 0 ? 'text-red-600' : 'text-yellow-600'
            }`} />
            <div className="flex-1">
              <p className={`font-semibold ${
                stats.near_deadline > 0 ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {stats.near_deadline > 0
                  ? `⚠️ ${stats.near_deadline} solicitação(ões) próxima(s) do fim do período!`
                  : `${stats.total} solicitação(ões) pendente(s)`
                }
              </p>
              <p className={`mt-0.5 ${
                stats.near_deadline > 0 ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {stats.near_deadline > 0
                  ? 'As solicitações só podem ser feitas nos dias 13 e 14.'
                  : 'Revise e aprove ou rejeite as solicitações pendentes.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pending' && (
        <>
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Carregando...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Nenhuma solicitação pendente.</p>
            </div>
          ) : (
        <div className="space-y-3">
          {requests.length > 1 && (
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-200 p-2">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900"
              >
                {selectedIds.size === requests.length ? (
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                ) : (
                  <Square className="w-4 h-4 text-gray-400" />
                )}
                <span>Selecionar todas ({requests.length})</span>
              </button>
            </div>
          )}
          {requests.map((request) => (
            <div
              key={request.id}
              className={`bg-white rounded-lg shadow-sm border-2 p-3 sm:p-4 hover:shadow-md transition-shadow text-xs sm:text-sm ${
                request.is_near_deadline
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-200'
              } ${selectedIds.has(request.id) ? 'ring-2 ring-blue-500' : ''}`}
            >
              {request.is_near_deadline && (
                <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <p className="text-xs font-semibold text-red-800">
                      ⚠️ Próximo do fim do período de solicitação!
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  <button
                    onClick={() => toggleSelect(request.id)}
                    className="mt-0.5"
                  >
                    {selectedIds.has(request.id) ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="font-semibold text-gray-900 truncate">{request.user_name}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3 text-gray-400" />
                        <span className="font-semibold text-gray-900">
                          {Number(request.amount || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-600">
                          {new Date(request.request_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      {request.ref_month && request.ref_year && (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">Ref:</span>
                          <span className="font-medium text-blue-600">
                            {monthNames[request.ref_month - 1]}/{request.ref_year}
                          </span>
                          {request.is_manual && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Manual</span>
                          )}
                        </div>
                      )}
                      {Number(request.base_salary || 0) > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md font-medium">
                            Salário: {Number(request.base_salary).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (Teto 40%: {Number(request.max_advance_limit || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                  request.is_near_deadline
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  <Clock className="w-3 h-3" />
                  <span className="hidden sm:inline">Aguardando</span>
                </span>
              </div>

              {request.notes && (
                <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Obs:</p>
                  <p className="text-xs text-gray-700">{request.notes}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 mt-3 pt-3 border-t">
                <button
                  onClick={() => handleApprove(request)}
                  disabled={isProcessing(request.id)}
                  className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5 text-xs sm:text-sm"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  {isProcessing(request.id) ? 'Aprovando...' : 'Aprovar'}
                </button>
                <div className="flex-1 flex gap-1.5">
                  <input
                    type="text"
                    placeholder="Motivo..."
                    value={rejectionReasons[request.id] || ''}
                    onChange={(e) => handleReasonChange(request.id, e.target.value)}
                    disabled={isProcessing(request.id)}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-xs disabled:opacity-50"
                  />
                  <button
                    onClick={() => handleReject(request)}
                    disabled={isProcessing(request.id) || !(rejectionReasons[request.id] || '').trim()}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 text-xs sm:text-sm"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Rejeitar</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
        </>
      )}

      {activeTab === 'approved' && (
        <>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 rounded-lg border flex items-center gap-1.5 text-xs sm:text-sm ${
                  showFilters || hasActiveFilters
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filtros
                {hasActiveFilters && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {Object.values(approvedFilters).filter(v => v !== '').length}
                  </span>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Limpar
                </button>
              )}
            </div>
            <span className="text-xs sm:text-sm text-gray-600">
              Total: {approvedPagination.total}
            </span>
          </div>

          {showFilters && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Mês</label>
                  <select
                    value={approvedFilters.ref_month}
                    onChange={(e) => handleFilterChange('ref_month', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {monthNames.map((name, idx) => (
                      <option key={idx} value={idx + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Ano</label>
                  <select
                    value={approvedFilters.ref_year}
                    onChange={(e) => handleFilterChange('ref_year', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">De</label>
                  <input
                    type="date"
                    value={approvedFilters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Até</label>
                  <input
                    type="date"
                    value={approvedFilters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {approvedLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Carregando...</p>
            </div>
          ) : approvedRequests.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Nenhuma aprovação encontrada.</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-3 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {approvedRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 text-xs sm:text-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{request.user_name}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                          <span className="text-gray-500">Solic:</span>
                          <span className="font-medium">{new Date(request.request_date).toLocaleDateString('pt-BR')}</span>
                          {request.approver_name && (
                            <>
                              <span className="text-gray-500">Aprov:</span>
                              <span className="font-medium">{request.approver_name.split(' ')[0]}</span>
                            </>
                          )}
                          {request.is_manual && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Manual</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-base sm:text-lg font-bold text-green-600">
                        {Number(request.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                      <p className="text-xs text-gray-500">
                        {monthNames[request.ref_month - 1]}/{request.ref_year}
                      </p>
                    </div>
                  </div>

                  {request.notes && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Obs:</p>
                      <p className="text-xs text-gray-700">{request.notes}</p>
                    </div>
                  )}

                  <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      Valor debitado no Caixa
                    </span>
                    <button
                      onClick={() => setConfirmAction({ type: 'cancel', request })}
                      disabled={isProcessing(request.id)}
                      className="px-2.5 py-1 text-xs border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg font-medium flex items-center gap-1 transition-colors"
                    >
                      <XCircle className="w-3.5 h-3.5 text-rose-600" />
                      {isProcessing(request.id) ? 'Estornando...' : 'Estornar / Cancelar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {approvedPagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="text-xs text-gray-600">
                Total: {approvedPagination.total}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-xs"
                >
                  <option value={10}>10/pag</option>
                  <option value={20}>20/pag</option>
                  <option value={50}>50/pag</option>
                </select>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-2 py-1 text-xs font-medium">
                  {approvedPagination.page}/{approvedPagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(approvedPagination.totalPages, p + 1))}
                  disabled={page === approvedPagination.totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ManualAdvanceModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries(['pending-advance-requests']);
          queryClient.invalidateQueries(['my-advance-request']);
          setPage(1);
        }}
      />

      <ConfirmModal
        show={!!confirmAction}
        onConfirm={() => {
          if (confirmAction?.type === 'approve') {
            setProcessingIds(prev => new Set([...prev, confirmAction.request.id]));
            approveMutation.mutate(confirmAction.request.id);
          } else if (confirmAction?.type === 'reject') {
            setProcessingIds(prev => new Set([...prev, confirmAction.request.id]));
            rejectMutation.mutate({ requestId: confirmAction.request.id, reason: rejectionReasons[confirmAction.request.id] });
          } else if (confirmAction?.type === 'cancel') {
            setProcessingIds(prev => new Set([...prev, confirmAction.request.id]));
            cancelMutation.mutate({
              requestId: confirmAction.request.id,
              reason: 'Cancelamento e estorno solicitado pela gerência via painel de adiantamentos'
            });
          } else if (confirmAction?.type === 'bulkApprove') {
            bulkApproveMutation.mutate(Array.from(selectedIds));
          }
          setConfirmAction(null);
        }}
        onCancel={() => setConfirmAction(null)}
        title={
          confirmAction?.type === 'approve'
            ? 'Aprovar Adiantamento'
            : confirmAction?.type === 'reject'
            ? 'Rejeitar Adiantamento'
            : confirmAction?.type === 'cancel'
            ? 'Cancelar e Estornar Adiantamento'
            : 'Aprovar Em Lote'
        }
        message={
          confirmAction?.type === 'approve'
            ? `Aprovar adiantamento de ${Number(confirmAction?.request?.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} para ${confirmAction?.request?.user_name}?`
            : confirmAction?.type === 'reject'
            ? 'Tem certeza que deseja rejeitar esta solicitação?'
            : confirmAction?.type === 'cancel'
            ? `Tem certeza que deseja cancelar o adiantamento de ${Number(confirmAction?.request?.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} de ${confirmAction?.request?.user_name}? O valor será devolvido imediatamente ao Caixa da Empresa.`
            : `Aprovar ${selectedIds.size} solicitação(ões)?`
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        type={confirmAction?.type === 'cancel' ? 'danger' : 'warning'}
      />
    </div>
  );
};

export default AdvanceApproval;
